import type { TextFieldPropOnChange, TextFieldPropStatus, TextFieldPropValue } from 'components/FieldNumberFormat';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import { Button, cnConsta, cnMixSpace } from 'uikit';

import cn from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import Styles from '../catalogCommonStyles.module.css';
import { CatalogEditValueInput } from '../CatalogEditValueInput';

import { VariableGroupsItem } from './components';
import { useGroupList, useCreateVariableGroup } from './hooks';

const cnListBox = cnConsta('ListBox');

interface IVariableGroupsProps {
    onClose: () => void;
    catalogUniqueId: string | undefined;
    methodology: ProjectMethodologyEnum;
    isGlobal?: boolean;
}

export const VariableGroups = ({ onClose, catalogUniqueId, methodology, isGlobal = false }: IVariableGroupsProps) => {
    const [isShowInput, setIsShowInput] = useState<boolean>(false);
    const [inputStatus, setInputStatus] = useState<TextFieldPropStatus | undefined>(undefined);
    const [newGroupName, setNewGroupName] = useState<TextFieldPropValue>(null);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const isLoaded = useRef<boolean>(false);

    const handleShowInput = useCallback(() => {
        setIsShowInput(true);
    }, []);

    const handleRemoveInput = useCallback(() => {
        setNewGroupName(null);
        setInputStatus(undefined);
        setIsShowInput(false);
        setIsSubmitted(false);
    }, []);

    const handleSubmitInput = useCallback(() => {
        if (newGroupName?.trim()) {
            setIsSubmitted(true);
        }
    }, [newGroupName]);

    const handleChangeNewGroupName = useCallback<TextFieldPropOnChange>(({ value }) => {
        setNewGroupName(value);
        setInputStatus(undefined);
        setIsSubmitted(false);
    }, []);

    const { groupListData, groupListStatus, updateGroupList } = useGroupList({
        catalogUniqueId,
        globalOnly: isGlobal,
        methodology,
    });
    const { isUniqueVariableGroupData, isUniqueVariableGroupStatus, createOtherVariableGroupHandler } =
        useCreateVariableGroup({
            groupName: newGroupName,
            removeInputHandler: handleRemoveInput,
            updateGroupList,
            catalogUniqueId,
            isGlobal,
            methodology,
        });

    useEffect(() => {
        if (groupListStatus === RequestStatus.SUCCESS) {
            isLoaded.current = true;
        }
    }, [groupListStatus]);

    useEffect(() => {
        if (
            isSubmitted &&
            isUniqueVariableGroupStatus === RequestStatus.SUCCESS &&
            isUniqueVariableGroupData === false
        ) {
            setInputStatus('alert');
        }
    }, [isSubmitted, isUniqueVariableGroupData, isUniqueVariableGroupStatus]);

    useEffect(() => {
        if (
            isSubmitted &&
            isUniqueVariableGroupStatus === RequestStatus.SUCCESS &&
            isUniqueVariableGroupData &&
            newGroupName
        ) {
            createOtherVariableGroupHandler(newGroupName);
        }
    }, [
        createOtherVariableGroupHandler,
        isSubmitted,
        isUniqueVariableGroupData,
        isUniqueVariableGroupStatus,
        newGroupName,
    ]);

    return (
        <ModalDialog
            isOpen
            isLoading={groupListStatus === RequestStatus.FETCHING && !isLoaded.current}
            title="Группы переменных"
            onClose={onClose}
            bodyWidth={600}
            withTitleCloseButton
            isOnClickOutsideDisable
            withBottomButtons={false}
        >
            <div className={Styles.modalBody}>
                <div className={cnMixSpace({ mB: 'm' })}>
                    <Button onClick={handleShowInput} label="Создать новую группу" size="s" view="secondary" />
                </div>
                <div className={cn(Styles.listWrapper, cnListBox({ size: 'm' }), cnMixSpace({ p: 'm' }))}>
                    {isShowInput && (
                        <CatalogEditValueInput
                            value={newGroupName}
                            onChange={handleChangeNewGroupName}
                            onCancel={handleRemoveInput}
                            onSubmit={handleSubmitInput}
                            status={inputStatus}
                            tooltipMsg="Группа с таким наименованием уже существует"
                            inputPlaceholder="Введите наименование группы"
                        />
                    )}
                    {groupListData.map(({ name, id, variableCount }) => (
                        <VariableGroupsItem
                            key={id}
                            id={id}
                            label={name}
                            count={variableCount}
                            updateGroupList={updateGroupList}
                            catalogUniqueId={catalogUniqueId}
                            isGlobal={isGlobal}
                            methodology={methodology}
                        />
                    ))}
                </div>
            </div>
        </ModalDialog>
    );
};

VariableGroups.displayName = 'VariableGroups';
